/**
 * The factory defaults for the report grids' four "View options", in ONE place.
 *
 * WHY THIS MODULE EXISTS (E4 verify pass, 2026-08-28)
 *
 * Each of these four values was written TWICE in each of the two grids: once as
 * the fallback in a `useState` initialiser, and once as a hardcoded literal in
 * "Reset grid layout". Four options x two sites x two grids = sixteen literals
 * that had to agree and nothing made them. A future change to one default would
 * reach the initialiser and not the reset, and the divergence would be silent —
 * the checkbox would come back in a state the grid never starts in.
 *
 * A plain leaf module rather than a helper exported from either grid, for the
 * same reason `columnDefinitionGate.js` is one: the flat grid and the grouped
 * grid must not import each other, and the 33 flat / 86 grouped split is exactly
 * where these two copies have repeatedly drifted.
 *
 * WHAT THIS IS NOT: it is NOT "the report's default", and the reset deliberately
 * does not restore the mount's OWN starting values. All four states initialise
 * from `initialToggles` (a saved view, or a reopened job), so a saved view that
 * seeds `hideZero: true` starts that mount with rows hidden — and the reset puts
 * it back to `false`, i.e. to MORE rows, not to the view's value.
 *
 * That divergence is deliberate and must stay this direction. Every value in
 * THIS object is the row-REVEALING one, so the worst a reset of the GRID's own
 * toggles can do is show a user rows they were not expecting. Deriving them from
 * `initialToggles` instead would make "Reset grid layout" capable of HIDING rows
 * on a financial grid — a user clicking a control named "reset" and losing money
 * rows off the bottom of a WIP or A/R report. Do not "fix" the asymmetry by
 * pointing the reset at `initialToggles`; fix the wording instead (both reset
 * tooltips say "the built-in defaults", not "the report's default", for this
 * reason).
 *
 * ⚠️ SCOPE CORRECTION, 2026-08-28. An earlier version of this paragraph claimed
 * the reset can NEVER hide rows on a financial grid. That is true of the values
 * in this object and false of the control as a whole: the same change that added
 * `onResetPageDisplayExtras` let a PAGE restore its own defaults, and Aged A/R's
 * `hideZero` genuinely defaults to `true` (ARReport.jsx, AR_DISPLAY_DEFAULTS).
 * So on report 21 the reset reveals rows for two controls and re-hides them for
 * a third. That is defensible there — it is the page's real starting state, and
 * the argument is written out at its own definition — but it means this file's
 * invariant is scoped to the grid's four toggles and does NOT extend to the page
 * hook. Do not cite this paragraph as a guarantee about the whole control.
 *
 * `forceCurrency` is null rather than false because it is TRI-state: null = auto
 * (however the server wrote the amount) and is the default; false = actively
 * strip the `$`, which is an override.
 *
 * `showThousands` (spec 212) is a plain on/off and defaults ON: spec 211 made
 * thousands separators the standard on every report figure, and OFF is the
 * user's override. It changes how figures READ, never which rows show, so the
 * row-revealing argument above does not apply to it.
 */
export const GRID_DISPLAY_DEFAULTS = Object.freeze({
  forceCurrency: null,
  showThousands: true,
  hideZero: false,
  hideInactive: false,
  agingMode: '',
  customDays: '',
})

/**
 * Spec 212: the per-report `preferencesStore` page flag that remembers a user's
 * explicit "Show commas in numbers" choice. A page flag rather than a new store
 * method, so none of the ~43 test mocks of the store had to change (the frozen
 * column's `pin:<id>` flags set that precedent). "Reset grid layout" clears it with
 * the rest of the page's flags (`resetPagePreferences`).
 */
export const THOUSANDS_PAGE_FLAG = 'thousands'

/**
 * The other half of the same reset contract: what the menu SAYS when a page
 * hands a grid `pageDisplayExtras` without an `onResetPageDisplayExtras`.
 *
 * The gap this closes was not a missing feature, it was a SILENT one. Aged A/R
 * supplied three page-owned controls — two of which hide rows — and no reset
 * hook, so "Reset grid layout" ran, reset the grid's own (suppressed, invisible)
 * copies of those toggles, and left the A/R grid still hiding money rows with
 * nothing on screen saying so. The reset appeared to work.
 *
 * So the omission is made VISIBLE rather than being defaulted away: a grid that
 * is handed controls it cannot reset renders this line in the same section, and
 * the next page to add a slot without a hook gets a caveat in the UI instead of
 * a reset that quietly under-delivers. Surfacing beats computing on a financial
 * grid — the failure mode of a missing hook is rows the user cannot get back.
 *
 * Kept here, next to the defaults, because both grids must render the SAME
 * sentence: the flat/grouped split (33 vs 86 reports) is exactly where these two
 * copies have repeatedly drifted, and a caveat that exists on one renderer only
 * is worse than none.
 */
export const PAGE_EXTRAS_NOT_RESET_NOTE =
  '“Reset grid layout” does not clear this report’s own options above.'

export default GRID_DISPLAY_DEFAULTS
